import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Chip, CircularProgress } from "@material-ui/core";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../Modal";
import useStyles, { WellnessSwitch } from "./styles";
import { translate as t } from "../../../../infrastructure/translate/localization";
import CobeeButton from "../../CobeeButton";
import iFeelImg from "../../../../assets/img/ifeel-benefit.png";
import CobeeTextField from "../../CobeeTextField";
import { MonetaryAmount } from "../../../domain/monetary-amount";

export default function IFeelConfigModal({
  callback,
  data,
  isLoading,
  productsWithRecurrentPayments,
}) {
  const {
    container,
    subtitle,
    vendorLogo,
    productSwitchContainer,
    configContainer,
    productIsSelected,
    buttonBar,
    chipContainer,
    chip,
    activeChip,
    percentageAppliedRow,
    loaderContainer,
    linkText,
    rowSpacing,
    attachmentRow,
    warningIcon,
  } = useStyles();

  const [callbackIsLoading, setCallbackIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(0);

  const { register, handleSubmit, getValues, setValue, watch } = useForm();

  useEffect(() => {
    if (!isLoading) {
      register("benefitConfigId");
      setValue(`benefitConfigId`, data.id);

      register(`vendorId`);
      setValue(`vendorId`, data.vendorId);

      data.products.forEach(({ active, config, id }, i) => {
        register(`products.${i}.active`);
        setValue(`products.${i}.active`, active);

        Object.keys(config).forEach((key) => {
          register(`products.${i}.productId`);
          setValue(`products.${i}.productId`, id);

          register(`products.${i}.config.${key}.enabled`);
          setValue(`products.${i}.config.${key}.enabled`, config[key].enabled);

          register(`products.${i}.config.${key}.subventionPercentage`);
          setValue(
            `products.${i}.config.${key}.subventionPercentage`,
            config[key].subventionPercentage
          );
        });
      });
    }
  }, [register, data, setValue, isLoading]);

  const handleProductSwitch = ({ index, value }) => {
    setValue(`products.${index}.active`, value);
  };

  const handleChipClick = ({ key }) => {
    const previousValue = getValues(
      `products.${selectedProduct}.config.${key}.enabled`
    );
    setValue(
      `products.${selectedProduct}.config.${key}.enabled`,
      !previousValue
    );
  };

  const onSubmit = async (formData) => {
    setCallbackIsLoading(true);
    await callback({ payload: formData });
    setCallbackIsLoading(false);
  };

  return (
    <Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12} container className={container}>
          <Grid item xs={6}>
            <Typography variant="h6">ifeel</Typography>
            <Typography variant="h6" className={subtitle}>
              {t("benefits-section.wellness-benefit.online-psychologists")}
            </Typography>
            <Typography variant="body1">
              {t("benefits-section.wellness-benefit.benefit-modal-tooltip")}
            </Typography>
          </Grid>

          <Grid item xs={6} container justify="flex-end">
            <img
              src={iFeelImg}
              alt="ifeel-vendor-logo"
              className={vendorLogo}
            />
          </Grid>

          {productsWithRecurrentPayments.length > 0 && (
            <Grid item xs={12} container className={rowSpacing}>
              <Grid item xs={1} container justify="center" alignItems="center">
                <FontAwesomeIcon
                  icon="exclamation-triangle"
                  className={warningIcon}
                />
              </Grid>
              <Grid item xs={10}>
                <Typography variant="h6">
                  {t(
                    "benefits-section.ifeel-benefit.products-with-recurrent-payments-are-blocked-message"
                  )}
                </Typography>
              </Grid>
            </Grid>
          )}

          {isLoading ? (
            <Grid
              item
              container
              xs={12}
              justify="center"
              className={loaderContainer}
            >
              <CircularProgress />
            </Grid>
          ) : (
            <Grid item xs={12} container>
              <Grid item xs={12} container>
                <Grid item xs={3}>
                  {data.products.map(({ name, id }, i) => (
                    <Grid
                      item
                      xs={12}
                      key={`wellness-product-${String(i)}`}
                      className={`${productSwitchContainer} ${
                        selectedProduct === i ? productIsSelected : ""
                      }`}
                      onClick={() => setSelectedProduct(i)}
                    >
                      <WellnessSwitch
                        disabled={productsWithRecurrentPayments.some(
                          (product) => product === id
                        )}
                        checked={watch(`products.${i}.active`) || false}
                        onChange={(e) =>
                          handleProductSwitch({
                            index: i,
                            value: e.target.checked,
                          })
                        }
                      />
                      {name}
                    </Grid>
                  ))}
                </Grid>

                <Grid item xs={9} container className={configContainer}>
                  <Grid item xs={12} className={rowSpacing}>
                    <Typography variant="h6">
                      {t("common-messages.product-code").toUpperCase()}:{" "}
                      {data.products[selectedProduct].code || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} className={rowSpacing}>
                    <Typography variant="h6">
                      {t("common-messages.price").toUpperCase()}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    container
                    justify="flex-end"
                    alignItems="flex-end"
                    className={rowSpacing}
                  >
                    <Typography variant="h6">
                      {MonetaryAmount.ofCurrency(
                        data.products[selectedProduct].fee
                      ).toUnitary()}{" "}
                      €
                    </Typography>
                    <Typography variant="body1">
                      /{t("common-messages.employee").toLowerCase()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t("common-messages.product-details")?.toUpperCase() ||
                        ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className={rowSpacing}>
                    <Typography variant="body1">
                      {data.products[selectedProduct].description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t(
                        "common-messages.informative-attachments"
                      ).toUpperCase()}
                    </Typography>
                  </Grid>
                  <Grid item container xs={12} className={rowSpacing}>
                    {data.products[selectedProduct].attachments.map(
                      ({ title, url }, i) => (
                        <Grid
                          item
                          xs={12}
                          container
                          key={`attachment-${String(i)}`}
                          className={attachmentRow}
                        >
                          <Grid item xs={6}>
                            <Typography variant="body1">{title}</Typography>
                          </Grid>
                          <Grid item xs={6} container justify="flex-end">
                            <a
                              href={url}
                              className={linkText}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t("common-messages.see-attachment")}
                            </a>
                          </Grid>
                        </Grid>
                      )
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      {t(
                        "benefits-section.wellness-benefit.who-will-benefit-message",
                        {
                          product: data.products[selectedProduct].name,
                        }
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} container className={chipContainer}>
                    {Object.keys(data.products[selectedProduct].config).map(
                      (key) => {
                        return (
                          <Chip
                            disabled={
                              productsWithRecurrentPayments.some(
                                (product) =>
                                  product === data.products[selectedProduct].id
                              ) &&
                              watch(
                                `products.${selectedProduct}.config.${key}.enabled`
                              )
                            }
                            key={key}
                            label={t(`common-messages.${key}`)}
                            onClick={() => handleChipClick({ key })}
                            className={`${chip} ${
                              watch(
                                `products.${selectedProduct}.config.${key}.enabled`
                              )
                                ? activeChip
                                : ""
                            }`}
                          />
                        );
                      }
                    )}
                  </Grid>
                  <Grid item xs={12} container>
                    {Object.keys(data.products[selectedProduct].config).map(
                      (key) => {
                        if (
                          watch(
                            `products.${selectedProduct}.config.${key}.enabled`
                          )
                        ) {
                          return (
                            <Grid
                              key={key}
                              container
                              item
                              xs={12}
                              className={percentageAppliedRow}
                            >
                              <Grid item xs={8} container justify="flex-start">
                                {t(
                                  "benefits-section.wellness-benefit.company-will-cover-message",
                                  { person: t(`common-messages.${key}`) }
                                )}
                              </Grid>
                              <Grid item xs={4} container alignItems="center">
                                <CobeeTextField
                                  styleType="underlined"
                                  value={watch(
                                    `products.${selectedProduct}.config.${key}.subventionPercentage`
                                  )}
                                  onChange={(e) =>
                                    setValue(
                                      `products.${selectedProduct}.config.${key}.subventionPercentage`,
                                      Number(e.target.value)
                                    )
                                  }
                                  type="number"
                                  minWidth="80%"
                                  min={0}
                                  max={100}
                                  step="0.01"
                                />
                                %
                              </Grid>
                            </Grid>
                          );
                        }
                        return null;
                      }
                    )}
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                container
                xs={12}
                justify="flex-end"
                className={buttonBar}
              >
                <CobeeButton
                  type="submit"
                  isLoading={callbackIsLoading}
                  disabled={callbackIsLoading}
                >
                  {t("actions.save")}
                </CobeeButton>
              </Grid>
            </Grid>
          )}
        </Grid>
      </form>
    </Modal>
  );
}

IFeelConfigModal.defaultProps = {
  data: {},
  isLoading: false,
  productsWithRecurrentPayments: [],
};

IFeelConfigModal.propTypes = {
  callback: PropTypes.func.isRequired,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  productsWithRecurrentPayments: PropTypes.array,
};
